import { useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { SigninWithCredentialsOptions } from '@okta/okta-auth-js';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { useAnalytics } from '@tcs/common/hooks/analytics';
import { useNavigate, useLocation } from 'react-router-dom';

enum oktaAuthStatuses {
  LOCKED = 'LOCKED_OUT',
  PASSWORD_EXPIRED = 'PASSWORD_EXPIRED',
}

interface Props {
  loading: boolean;
  signOut: () => void;
  signIn: (credentials: SigninWithCredentialsOptions) => void;
  signOutWithRedirectUrl: (redirectUrl: string, state: any) => void;
  isUserLocked: boolean;
  isPasswordExpired: boolean;
}

export const useAuthentication = (): Props => {
  const [loading, setLoading] = useState(false);
  const [isUserLocked, setIsLocked] = useState(false);
  const [isPasswordExpired, setIsPasswordExpired] = useState(false);
  const { oktaAuth } = useOktaAuth();
  const loc = useLocation();
  const analytics = useAnalytics();
  const launchDarklyClient = useLDClient();
  const nav = useNavigate();

  const signIn = async (credentials: SigninWithCredentialsOptions) => {
    const serializedCredentials = {
      ...credentials,
      username: credentials.username.trim(),
    };
    try {
      setLoading(true);
      setIsLocked(false);
      setIsPasswordExpired(false);
      setIsLocked(false);
      oktaAuth.removeOriginalUri(oktaAuth.options.state);
      const { sessionToken, status } = await oktaAuth.signInWithCredentials(
        serializedCredentials
      );

      if (status === oktaAuthStatuses.LOCKED) {
        setIsLocked(true);
        throw new Error('Account is locked');
      }
      if (status === oktaAuthStatuses.PASSWORD_EXPIRED) {
        setIsPasswordExpired(true);
        throw new Error('Password is expired');
      }

      if (!sessionToken) {
        throw new Error('No Session obtained ');
      }

      // pass along the original URL - only do so if the URL and accounts match
      if (loc.pathname.indexOf('accounts') > -1) {
        if (loc.pathname.indexOf(credentials.username.trim()) === -1) {
          loc.pathname = '';
        }
      }
      oktaAuth.signInWithRedirect({
        sessionToken,
        originalUri: loc.pathname,
      });
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      // use a known key, don't allow it to generate a random UUID per user on logout
      // similar to what we do for login with FeatureFlagProvider
      launchDarklyClient.identify({
        key: 'forgeclient',
        anonymous: true,
      });
      await oktaAuth.signOut();
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      nav('/login', { replace: true });
      analytics.resetUser();
      setLoading(false);
    }
  };

  const signOutWithRedirectUrl = async (redirectUrl: string, state: string) => {
    try {
      setLoading(true);
      // use a known key, don't allow it to generate a random UUID per user on logout
      // similar to what we do for login with FeatureFlagProvider
      launchDarklyClient.identify({
        key: 'forgeclient',
        anonymous: true,
      });
      await oktaAuth.signOut({ postLogoutRedirectUri: redirectUrl, state });
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      analytics.resetUser();
      setLoading(false);
    }
  };

  return {
    loading,
    signIn,
    signOut,
    signOutWithRedirectUrl,
    isUserLocked,
    isPasswordExpired,
  };
};

export default useAuthentication;
